import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const ARM_RADIUS = 150
const ARM_TIME_MS = 650
const CHARGE_TIME_MS = 520
const RECOVER_TIME_MS = 360
const TRACK_SPEED = 0.075
const DRIFT_SPEED_MULTIPLIER = 0.55
const CHARGE_SPEED = 6.8
const PLAYER_STANDOFF_X = 10
const RETREAT_STANDOFF_X = 118
const RETREAT_SPEED = 3.4
const MINE_WARNING_COLOR = 0xfacc15
const MINE_DANGER_COLOR = 0xff3b30

type SpikeMinePhase = "dormant" | "armed" | "charging" | "recovering"

export class SpikeMineEnemy extends StatefulEnemyRuntime<SpikeMinePhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<SpikeMinePhase>[] = [
      {
        name: "dormant",
        behaviorState: "flying",
        animationState: "idle",
        update: (context) => this.updateDormant(context),
        onDamage: () => "armed",
      },
      {
        name: "armed",
        behaviorState: "focusing",
        animationState: "focusing",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(MINE_WARNING_COLOR)
          context.vfx.emitImpactBurst(
            context.sprite.x,
            context.sprite.y,
            MINE_WARNING_COLOR,
            0.75,
          )
        },
        update: (context) => this.updateArmed(context),
        onDamage: () => "charging",
      },
      {
        name: "charging",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: false,
        enter: (context) => this.enterCharging(context),
        update: (context) => this.updateCharging(context),
      },
      {
        name: "recovering",
        behaviorState: "retreating",
        animationState: "retreating",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0xfb923c)
        },
        update: (context) => this.updateRecovering(context),
      },
    ]

    super(sprite, enemyData, "dormant", states)
  }

  public override onHeroCollision():
    | "damage-only"
    | "damage-and-defeat"
    | null {
    return "damage-and-defeat"
  }

  private updateDormant(
    context: EnemyStateContext<SpikeMinePhase>,
  ): SpikeMinePhase | null {
    this.applyMineDrift(context, 1)
    context.sprite.clearTint()

    if (!this.isOnScreen(context.sprite)) return null
    if (this.getPlayerDistance(context) <= ARM_RADIUS) {
      return "armed"
    }

    return null
  }

  private updateArmed(
    context: EnemyStateContext<SpikeMinePhase>,
  ): SpikeMinePhase | null {
    this.applyMineDrift(context, DRIFT_SPEED_MULTIPLIER)
    this.trackPlayerY(context, TRACK_SPEED)

    const pulse = 0.65 + Math.sin(context.stateTime / 55) * 0.35
    context.sprite.setTint(pulse > 0.4 ? MINE_DANGER_COLOR : MINE_WARNING_COLOR)

    if (context.stateTime >= ARM_TIME_MS) {
      return "charging"
    }

    return null
  }

  private enterCharging(context: EnemyStateContext<SpikeMinePhase>): void {
    const targetX = Math.max(
      context.playerX + PLAYER_STANDOFF_X,
      context.sprite.x - 180,
    )
    const angle = Phaser.Math.Angle.Between(
      context.sprite.x,
      context.sprite.y,
      targetX,
      context.playerY,
    )

    context.enemyData.vx = Math.cos(angle) * CHARGE_SPEED
    context.enemyData.vy = Math.sin(angle) * CHARGE_SPEED
    context.sprite.setTint(MINE_DANGER_COLOR)
    context.vfx.emitImpactBurst(
      context.sprite.x,
      context.sprite.y,
      MINE_DANGER_COLOR,
      1.1,
    )
  }

  private updateCharging(
    context: EnemyStateContext<SpikeMinePhase>,
  ): SpikeMinePhase | null {
    if (
      context.sprite.x <= context.playerX + PLAYER_STANDOFF_X ||
      context.stateTime >= CHARGE_TIME_MS
    ) {
      this.pinInFrontOfPlayer(context)
      return "recovering"
    }

    return null
  }

  private updateRecovering(
    context: EnemyStateContext<SpikeMinePhase>,
  ): SpikeMinePhase | null {
    this.recoverToStandoff(context)

    if (
      context.stateTime >= RECOVER_TIME_MS &&
      context.sprite.x >= context.playerX + RETREAT_STANDOFF_X
    ) {
      return "armed"
    }

    return null
  }

  private applyMineDrift(
    context: EnemyStateContext<SpikeMinePhase>,
    speedMultiplier: number,
  ): void {
    const { sprite, enemyData, time, scrollCompensation } = context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "stationary-mine") return

    enemyData.vx = scrollCompensation - spec.driftSpeed * speedMultiplier

    const homeY = enemyData.homeY ?? sprite.y
    const targetY =
      homeY + Math.sin(time / spec.bobPeriodMs) * spec.bobAmplitude
    enemyData.vy = (targetY - sprite.y) * 0.045
  }

  private trackPlayerY(
    context: EnemyStateContext<SpikeMinePhase>,
    amount: number,
  ): void {
    const desiredVy = Phaser.Math.Clamp(
      (context.playerY - context.sprite.y) * amount,
      -2.2,
      2.2,
    )
    context.enemyData.vy = Phaser.Math.Linear(
      context.enemyData.vy,
      desiredVy,
      0.4,
    )
  }

  private recoverToStandoff(context: EnemyStateContext<SpikeMinePhase>): void {
    this.trackPlayerY(context, TRACK_SPEED * 0.55)

    const targetX = Math.min(
      GAME_WIDTH - 60,
      context.playerX + RETREAT_STANDOFF_X,
    )
    const retreatDirection = context.sprite.x < targetX ? 1 : -0.35
    context.enemyData.vx =
      context.scrollCompensation + retreatDirection * RETREAT_SPEED
    context.enemyData.vy *= 0.82
  }

  private pinInFrontOfPlayer(context: EnemyStateContext<SpikeMinePhase>): void {
    const minX = context.playerX + PLAYER_STANDOFF_X
    if (context.sprite.x < minX) {
      context.sprite.x = minX
      context.enemyData.x = minX
    }
    context.enemyData.vx = Math.max(0, context.enemyData.vx)
  }

  private getPlayerDistance(
    context: EnemyStateContext<SpikeMinePhase>,
  ): number {
    return Phaser.Math.Distance.Between(
      context.sprite.x,
      context.sprite.y,
      context.playerX,
      context.playerY,
    )
  }

  private isOnScreen(sprite: GameSprite): boolean {
    return (
      sprite.x > 0 &&
      sprite.x < GAME_WIDTH &&
      sprite.y > 0 &&
      sprite.y < GAME_HEIGHT
    )
  }
}
